import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Pause,
  Pencil,
  Play,
  Plus,
  Repeat,
  Search,
  Trash2,
  X,
  Zap,
} from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Badge, Button, Card, EmptyState, PageHeader, TableSkeleton } from "../components/ui";
import { formatDate, formatMoney } from "../lib/format";
import { isManagerOrAbove } from "../lib/people";
import { FREQUENCIES, SCHEDULE_FILTERS, SCHEDULE_STATUS_TONE } from "../lib/recurring";
import { useAccountTimeZone } from "../lib/useAccountTimeZone";
import type { BillingSchedule, BillingScheduleStatus, ProductOption, Tax } from "../api/types";
import type { BillingScheduleInput, BillingScheduleItemInput } from "../api/client";

type Line = {
  product: string;
  description: string;
  quantity: string;
  unit_price: string;
  discount_amount: string;
  tax: string;
};

/**
 * Recurring invoices: memberships and retainers on a schedule.
 *
 * A schedule is a template; the invoices it produces are generated by the
 * scheduler (or the "Run now" action), never edited here. Pausing and resuming
 * are the two states that matter day to day.
 */
export function RecurringPage() {
  const { user } = useAuth();
  const timeZone = useAccountTimeZone();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState<BillingSchedule | null>(null);
  const [creating, setCreating] = useState(false);

  const search = params.get("search") ?? "";
  const status = params.get("status") ?? "";
  const cursor = params.get("cursor") ?? "";
  const [draft, setDraft] = useState(search);
  const debounceTimer = useRef<number | undefined>(undefined);

  useEffect(() => setDraft(search), [search]);
  useEffect(() => () => window.clearTimeout(debounceTimer.current), []);

  function update(next: Record<string, string>) {
    setParams(
      (prev) => {
        const merged = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(next)) {
          if (value) merged.set(key, value);
          else merged.delete(key);
        }
        return merged;
      },
      { replace: true },
    );
  }

  function onDraftChange(value: string) {
    setDraft(value);
    window.clearTimeout(debounceTimer.current);
    debounceTimer.current = window.setTimeout(
      () => update({ search: value, cursor: "" }),
      250,
    );
  }

  const query = useQuery({
    queryKey: ["billing-schedules", search, status, cursor],
    queryFn: ({ signal }) =>
      api.billingSchedules(
        {
          search: search || undefined,
          status: (status || undefined) as BillingScheduleStatus | undefined,
          cursor: cursor || undefined,
          page_size: 25,
        },
        signal,
      ),
    placeholderData: (prev) => prev,
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ["billing-schedules"] });
  const canWrite = isManagerOrAbove(user?.role);

  const action = useMutation({
    mutationFn: ({ id, verb }: { id: string; verb: "pause" | "resume" | "cancel" | "generate" }) => {
      if (verb === "pause") return api.pauseBillingSchedule(id);
      if (verb === "resume") return api.resumeBillingSchedule(id);
      if (verb === "cancel") return api.cancelBillingSchedule(id);
      return api.generateBillingSchedule(id);
    },
    onSuccess: invalidate,
  });

  const schedules = query.data?.results ?? [];
  const nextCursor = cursorFrom(query.data?.next);
  const filtering = Boolean(search || status);

  return (
    <>
      <PageHeader
        title="Recurring invoices"
        subtitle="Memberships and retainers that bill themselves on a schedule."
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => { setCreating(true); setEditing(null); }}>
              <Plus size={14} /> New schedule
            </Button>
          ) : undefined
        }
      />

      {creating || editing ? (
        <ScheduleForm
          schedule={editing}
          onDone={() => {
            setCreating(false);
            setEditing(null);
            invalidate();
          }}
          onCancel={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="search"
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            placeholder="Search schedules"
            aria-label="Search schedules"
            className="w-full rounded-md border-0 bg-white py-2 pl-9 pr-3 text-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          />
        </div>
        <div className="flex gap-1">
          {SCHEDULE_FILTERS.map((value) => (
            <button
              key={value || "all"}
              onClick={() => update({ status: value, cursor: "" })}
              className={
                status === value
                  ? "rounded-full bg-slate-900 px-3 py-1.5 text-xs font-medium capitalize text-white"
                  : "rounded-full bg-white px-3 py-1.5 text-xs font-medium capitalize text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
              }
            >
              {value || "All"}
            </button>
          ))}
        </div>
        {filtering ? (
          <Button variant="ghost" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
            <X size={14} /> Clear
          </Button>
        ) : null}
      </div>

      {action.isError ? (
        <p role="alert" className="mb-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {action.error instanceof ApiError ? action.error.message : "That action failed."}
        </p>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
        {query.isPending ? (
          <TableSkeleton rows={6} />
        ) : query.isError ? (
          <EmptyState
            title="Could not load schedules"
            body={query.error instanceof ApiError ? query.error.message : "Please try again."}
          />
        ) : schedules.length === 0 ? (
          <EmptyState
            icon={<Repeat size={28} />}
            title={filtering ? "Nothing matches that" : "No recurring schedules yet"}
            body={
              filtering
                ? "Try a different name, or clear the filters."
                : "Set one up to bill a retainer or membership automatically."
            }
          />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Customer</th>
                <th className="px-4 py-2.5">Frequency</th>
                <th className="px-4 py-2.5">Next run</th>
                <th className="px-4 py-2.5">Last invoice</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {schedules.map((schedule) => (
                <tr key={schedule.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Link
                      to={`/billing/recurring/${schedule.id}`}
                      className="text-sm font-medium text-slate-900 hover:underline"
                    >
                      {schedule.name}
                    </Link>
                    <p className="text-xs text-slate-500">
                      {schedule.occurrences_generated} generated
                      {schedule.total_occurrences ? ` of ${schedule.total_occurrences}` : ""}
                    </p>
                    {schedule.last_error ? (
                      <p className="text-xs text-rose-600">Last run failed</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">{schedule.contact_name || "—"}</td>
                  <td className="px-4 py-3 text-sm capitalize text-slate-600">
                    {schedule.frequency.replace(/_/g, " ")}
                    {schedule.interval_count > 1 ? ` ×${schedule.interval_count}` : ""}
                  </td>
                  <td className="px-4 py-3 text-sm tabular-nums text-slate-600">
                    {formatDate(schedule.next_invoice_date, timeZone)}
                  </td>
                  <td className="px-4 py-3 text-sm tabular-nums">
                    {schedule.last_invoice ? (
                      <Link
                        to={`/billing/invoices/${schedule.last_invoice.id}`}
                        className="text-slate-700 hover:underline"
                      >
                        {schedule.last_invoice.number || "Draft"}
                      </Link>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={SCHEDULE_STATUS_TONE[schedule.status] ?? "slate"}>
                      <span className="capitalize">{schedule.status}</span>
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    {canWrite ? (
                      <div className="flex items-center justify-end gap-1">
                        <IconAction
                          title="Edit"
                          onClick={() => { setEditing(schedule); setCreating(false); }}
                        >
                          <Pencil size={14} />
                        </IconAction>
                        {schedule.status === "active" ? (
                          <IconAction title="Pause" onClick={() => action.mutate({ id: schedule.id, verb: "pause" })}>
                            <Pause size={14} />
                          </IconAction>
                        ) : null}
                        {schedule.status === "paused" ? (
                          <IconAction title="Resume" onClick={() => action.mutate({ id: schedule.id, verb: "resume" })}>
                            <Play size={14} />
                          </IconAction>
                        ) : null}
                        {schedule.status !== "cancelled" && schedule.status !== "completed" ? (
                          <>
                            <IconAction title="Run now" onClick={() => action.mutate({ id: schedule.id, verb: "generate" })}>
                              <Zap size={14} />
                            </IconAction>
                            <IconAction title="Cancel" onClick={() => action.mutate({ id: schedule.id, verb: "cancel" })}>
                              <Trash2 size={14} />
                            </IconAction>
                          </>
                        ) : null}
                      </div>
                    ) : (
                      <button
                        onClick={() => navigate(`/billing/recurring/${schedule.id}`)}
                        className="text-xs font-medium text-slate-500 hover:text-slate-900"
                      >
                        View
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <nav className="mt-4 flex items-center justify-between">
        <Button onClick={() => update({ cursor: "" })} disabled={!cursor || query.isFetching}>
          <ChevronLeft size={16} /> First
        </Button>
        <span className="text-xs text-slate-500">
          {query.isFetching ? "Loading." : cursor ? "Next page" : "First page"}
        </span>
        <Button
          onClick={() => nextCursor && update({ cursor: nextCursor })}
          disabled={!nextCursor || query.isFetching}
        >
          Next <ChevronRight size={16} />
        </Button>
      </nav>
    </>
  );
}

function IconAction({
  title,
  onClick,
  children,
}: {
  title: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
    >
      {children}
    </button>
  );
}

function blankLine(): Line {
  return { product: "", description: "", quantity: "1.00", unit_price: "", discount_amount: "", tax: "" };
}

export function lineFromItem(item: BillingSchedule["items"][number]): Line {
  return {
    product: item.product ?? "",
    description: item.description,
    quantity: item.quantity,
    unit_price: item.unit_price?.amount ?? "",
    discount_amount: item.discount_amount?.amount ?? "",
    tax: item.tax ?? "",
  };
}

export function ScheduleForm({
  schedule,
  onDone,
  onCancel,
}: {
  schedule: BillingSchedule | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const contacts = useQuery({
    queryKey: ["contacts", "schedule-picker"],
    queryFn: ({ signal }) => api.contacts({ page_size: 200 }, signal),
    staleTime: 60_000,
  });
  const taxes = useQuery({
    queryKey: ["taxes"],
    queryFn: ({ signal }) => api.taxes(signal),
    staleTime: 5 * 60_000,
  });
  const products = useQuery({
    queryKey: ["product-options"],
    queryFn: ({ signal }) => api.productOptions(signal),
    staleTime: 5 * 60_000,
  });

  const [contact, setContact] = useState(schedule?.contact ?? "");
  const [name, setName] = useState(schedule?.name ?? "");
  const [frequency, setFrequency] = useState<string>(schedule?.frequency ?? "monthly");
  const [interval, setInterval] = useState(String(schedule?.interval_count ?? 1));
  const [startDate, setStartDate] = useState(schedule?.start_date ?? today());
  const [endDate, setEndDate] = useState(schedule?.end_date ?? "");
  const [nextDate, setNextDate] = useState(schedule?.next_invoice_date ?? "");
  const [totalOccurrences, setTotalOccurrences] = useState(
    schedule?.total_occurrences ? String(schedule.total_occurrences) : "",
  );
  const [autoGenerate, setAutoGenerate] = useState(schedule?.auto_generate ?? true);
  const [terms, setTerms] = useState(String(schedule?.payment_terms_days ?? 0));
  const [lines, setLines] = useState<Line[]>(() =>
    schedule?.items.length ? schedule.items.map(lineFromItem) : [blankLine()],
  );

  function buildBody(): BillingScheduleInput {
    const items: BillingScheduleItemInput[] = lines
      .filter((line) => line.description.trim())
      .map((line, index) => ({
        product: line.product || null,
        description: line.description.trim(),
        quantity: line.quantity || "1.00",
        unit_price: { amount: line.unit_price || "0.00" },
        discount_amount: { amount: line.discount_amount || "0.00" },
        tax: line.tax || null,
        sort_order: index,
      }));
    return {
      contact,
      name,
      frequency,
      interval_count: Number(interval) || 1,
      start_date: startDate,
      end_date: endDate || null,
      next_invoice_date: nextDate || startDate,
      total_occurrences: totalOccurrences ? Number(totalOccurrences) : null,
      auto_generate: autoGenerate,
      payment_terms_days: Number(terms) || 0,
      items,
    };
  }

  const preview = useQuery({
    queryKey: [
      "billing-schedule-preview",
      contact,
      frequency,
      interval,
      startDate,
      endDate,
      nextDate,
      terms,
      lines,
    ],
    queryFn: () => api.previewBillingSchedule(buildBody()),
    enabled: Boolean(contact) && lines.some((line) => line.description.trim()),
    retry: false,
  });

  const save = useMutation({
    mutationFn: () =>
      schedule
        ? api.updateBillingSchedule(schedule.id, buildBody())
        : api.createBillingSchedule(buildBody()),
    onSuccess: onDone,
  });

  const valid = Boolean(contact) && name.trim().length > 0 && lines.some((l) => l.description.trim());

  function updateLine(index: number, patch: Partial<Line>) {
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  }

  function pickProduct(index: number, productId: string) {
    const product = products.data?.find((row: ProductOption) => row.id === productId);
    updateLine(index, {
      product: productId,
      ...(product
        ? {
            description: product.name,
            unit_price: product.price?.amount ?? "",
          }
        : {}),
    });
  }

  return (
    <Card className="mb-6">
      <h2 className="mb-4 text-sm font-semibold">
        {schedule ? `Edit ${schedule.name}` : "New recurring schedule"}
      </h2>
      <form
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          if (valid) save.mutate();
        }}
        className="space-y-4"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Customer">
            <select value={contact} onChange={(e) => setContact(e.target.value)} className={INPUT}>
              <option value="">Select a customer…</option>
              {contacts.data?.results.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.display_name || row.company_name || row.last_name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Schedule name">
            <input value={name} onChange={(e) => setName(e.target.value)} className={INPUT} />
          </Field>
          <Field label="Frequency">
            <select value={frequency} onChange={(e) => setFrequency(e.target.value)} className={INPUT}>
              {FREQUENCIES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Every N periods">
            <input
              inputMode="numeric"
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
              className={INPUT}
            />
          </Field>
          <Field label="Start date">
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={INPUT} />
          </Field>
          <Field label="End date (optional)">
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={INPUT} />
          </Field>
          <Field label="Next invoice date">
            <input type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} className={INPUT} />
          </Field>
          <Field label="Stop after N invoices (optional)">
            <input
              inputMode="numeric"
              value={totalOccurrences}
              onChange={(e) => setTotalOccurrences(e.target.value)}
              className={INPUT}
            />
          </Field>
          <Field label="Payment terms (days)">
            <input inputMode="numeric" value={terms} onChange={(e) => setTerms(e.target.value)} className={INPUT} />
          </Field>
          <label className="flex items-center gap-2 self-end text-sm text-slate-700">
            <input
              type="checkbox"
              checked={autoGenerate}
              onChange={(e) => setAutoGenerate(e.target.checked)}
              className="size-4 rounded border-slate-300"
            />
            Issue the invoice automatically
          </label>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold">Lines</h3>
            <Button onClick={() => setLines((prev) => [...prev, blankLine()])}>
              <Plus size={14} /> Add line
            </Button>
          </div>
          <div className="space-y-3">
            {lines.map((line, index) => (
              <div key={index} className="grid gap-3 rounded-md border border-slate-200 p-3 sm:grid-cols-12">
                <div className="sm:col-span-3">
                  <Field label="Product">
                    <select
                      value={line.product}
                      onChange={(e) => pickProduct(index, e.target.value)}
                      className={INPUT}
                    >
                      <option value="">Custom line</option>
                      {products.data?.map((product: ProductOption) => (
                        <option key={product.id} value={product.id}>
                          {product.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <div className="sm:col-span-3">
                  <Field label="Description">
                    <input
                      value={line.description}
                      onChange={(e) => updateLine(index, { description: e.target.value })}
                      className={INPUT}
                    />
                  </Field>
                </div>
                <div className="sm:col-span-1">
                  <Field label="Qty">
                    <input
                      inputMode="decimal"
                      value={line.quantity}
                      onChange={(e) => updateLine(index, { quantity: e.target.value })}
                      className={INPUT}
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Unit price">
                    <input
                      inputMode="decimal"
                      value={line.unit_price}
                      onChange={(e) => updateLine(index, { unit_price: e.target.value })}
                      className={INPUT}
                    />
                  </Field>
                </div>
                <div className="sm:col-span-1">
                  <Field label="Disc">
                    <input
                      inputMode="decimal"
                      value={line.discount_amount}
                      onChange={(e) => updateLine(index, { discount_amount: e.target.value })}
                      className={INPUT}
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Tax">
                    <select
                      value={line.tax}
                      onChange={(e) => updateLine(index, { tax: e.target.value })}
                      className={INPUT}
                    >
                      <option value="">No tax</option>
                      {taxes.data?.map((tax: Tax) => (
                        <option key={tax.id} value={tax.id}>
                          {tax.name} ({Number(tax.rate ?? 0)}%)
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
              </div>
            ))}
          </div>
        </div>

        {contact && lines.some((l) => l.description.trim()) ? (
          <Card className="bg-slate-50/60">
            <h3 className="mb-3 text-sm font-semibold">Next invoice preview</h3>
            {preview.isPending ? (
              <p className="text-sm text-slate-500">Working out the figures…</p>
            ) : preview.isError ? (
              <p role="alert" className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
                {preview.error instanceof ApiError
                  ? preview.error.message
                  : "This schedule cannot be priced yet."}
              </p>
            ) : preview.data ? (
              <dl className="space-y-2 text-sm">
                <PreviewRow label="Issue date" value={formatDate(preview.data.issue_date)} />
                <PreviewRow label="Due" value={formatDate(preview.data.due_on)} />
                <PreviewRow label="Subtotal" value={formatMoney(preview.data.subtotal)} />
                <PreviewRow label="Discount" value={formatMoney(preview.data.discount_amount)} />
                <PreviewRow label="Tax" value={formatMoney(preview.data.tax_amount)} />
                <div className="border-t border-slate-200 pt-2">
                  <PreviewRow label="Total" value={formatMoney(preview.data.total_amount)} strong />
                </div>
              </dl>
            ) : null}
          </Card>
        ) : null}

        {save.isError ? (
          <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {save.error instanceof ApiError ? save.error.message : "Could not save the schedule."}
          </p>
        ) : null}

        <div className="flex items-center gap-2">
          <Button
            type="submit"
            variant="primary"
            disabled={save.isPending || !valid || preview.isError}
          >
            {save.isPending ? "Saving…" : schedule ? "Save schedule" : "Create schedule"}
          </Button>
          <Button onClick={onCancel} disabled={save.isPending}>
            Cancel
          </Button>
          {!valid ? (
            <span className="text-xs text-slate-500">A customer, a name and one line are required.</span>
          ) : null}
        </div>
      </form>
    </Card>
  );
}

function PreviewRow({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-slate-500">{label}</dt>
      <dd className={strong ? "font-semibold text-slate-900" : "text-slate-800"}>{value}</dd>
    </div>
  );
}

function today(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

const INPUT =
  "w-full rounded-md border-0 bg-white px-2.5 py-1.5 text-sm text-slate-900 ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900";

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </span>
      {children}
    </label>
  );
}

/** `next` is an absolute URL; the cursor is its trailing query parameter. */
function cursorFrom(next: string | null | undefined): string | undefined {
  if (!next) return undefined;
  return new URL(next, window.location.origin).searchParams.get("cursor") ?? undefined;
}
